/**
 * Public marketing copy for the login / suite landing page.
 * Prices and limits mirror packages/api plan-catalog.ts (Aug 2026, ~50% margin model).
 * Keep this in sync when the catalog changes.
 */

export type MarketingPlanKey = 'starter' | 'professional' | 'business';

export interface MarketingPlan {
  key: MarketingPlanKey;
  name: string;
  blurb: string;
  priceMonthlyUsd: number;
  priceMonthlyZar: number;
  popular?: boolean;
  limits: {
    birds: string;
    users: string;
    knowledgeSources: string;
    aiInvocations: string;
    storage: string;
    alwaysOn: string;
  };
}

export const MARKETING_PLANS: MarketingPlan[] = [
  {
    key: 'starter',
    name: 'Starter',
    blurb: 'Full suite for a small aviary on shared hosting with light AI use.',
    priceMonthlyUsd: 16,
    priceMonthlyZar: 299,
    limits: {
      birds: 'Up to 200 birds',
      users: '2 users',
      knowledgeSources: '10 knowledge sources',
      aiInvocations: '250 AI calls / month',
      storage: '2 GB photos & documents',
      alwaysOn: 'Autostop hosting (idle scale-down)',
    },
  },
  {
    key: 'professional',
    name: 'Professional',
    blurb: 'Most breeders: higher AI and storage budgets for active seasons.',
    priceMonthlyUsd: 25,
    priceMonthlyZar: 479,
    popular: true,
    limits: {
      birds: 'Up to 2,500 birds',
      users: '10 users',
      knowledgeSources: '100 knowledge sources',
      aiInvocations: '2,500 AI calls / month',
      storage: '25 GB photos & documents',
      alwaysOn: 'Autostop hosting (idle scale-down)',
    },
  },
  {
    key: 'business',
    name: 'Business',
    blurb: 'Commercial scale with room for isolation and always-on compute when needed.',
    priceMonthlyUsd: 50,
    priceMonthlyZar: 929,
    limits: {
      birds: 'Unlimited birds',
      users: 'Unlimited users',
      knowledgeSources: 'Unlimited knowledge sources',
      aiInvocations: '15,000 AI calls / month',
      storage: '100 GB photos & documents',
      alwaysOn: 'Always-on compute allowed',
    },
  },
];

export const SUITE_MODULES = [
  {
    title: 'Flock & species',
    body: 'Bird records, photos, cages, provenance, mutations, and species reference data — including AI-assisted enrichment that you review before saving.',
  },
  {
    title: 'Breeding & chicks',
    body: 'Breeding pairs, clutches, egg status through hatch and fledge, unbanded chick tracking, and banding into the main flock.',
  },
  {
    title: 'Health & husbandry',
    body: 'Health tests, treatments, disease workflows, nutrition and feed stock, tasks, and due-date reminders tied to your birds.',
  },
  {
    title: 'Accounts & marketplace',
    body: 'Sales, purchases, suppliers, customers, basic accounting, and optional marketplace listings / messaging for birds you sell.',
  },
  {
    title: 'BirdChatter & knowledge',
    body: 'In-app AI assistant grounded in your Knowledge Library and BirdMan data. AI is assistive — operators own every save.',
  },
  {
    title: 'Field & ops',
    body: 'Android field app for cage-side work, notifications, reports, and an admin operations console for backups and instance health.',
  },
];

export const HOW_BIRDMAN_WORKS = [
  'One dedicated BirdMan instance per customer (your own app URL and database isolation level for your plan).',
  'Every paid plan includes the full product suite. Plans differ by usage limits that drive hosting and AI cost — not by hiding modules.',
  'AI features stay included without surprise module upsells.',
];

export const SUBSCRIBE_STEPS = [
  {
    title: 'Trial the full suite',
    body: 'New instances typically start with a 14–30 day full-suite trial so you can load birds, run a clutch cycle, and try BirdChatter before paying.',
  },
  {
    title: 'Choose a usage tier',
    body: 'Pick Starter, Professional, or Business based on flock size, team size, AI volume, and storage — not feature unlocks. Upgrade when meters run short.',
  },
  {
    title: 'Pay via Stripe Checkout',
    body: 'When billing is enabled on your instance, checkout opens a Stripe-hosted payment page. Card details never touch BirdMan servers. Failed payments enter a short grace period with email notices.',
  },
  {
    title: 'Manage or cancel in Billing Portal',
    body: 'Subscribers open the Stripe Customer Portal from Settings → Subscription to update payment method, download invoices, or cancel at period end.',
  },
];

export const CONTRACTING_NOTES = [
  'Use of BirdMan is governed by the Terms of Service and Privacy Policy (POPIA / GDPR-oriented templates). Links appear when your operator has published them.',
  'A Data Processing Agreement (DPA) and subprocessors list are available for business customers who need them on file.',
  'Optional paid onboarding / data migration is offered separately (indicative list price USD 149) when you need help importing flock history.',
  'Annual billing is offered as two months free (~17% off monthly list) when configured in Stripe.',
  'Prices are indicative list rates in USD with ZAR approximations; your Stripe invoice currency and VAT/VAT-like tax depend on how the operator configures Stripe Tax.',
];

export const UPDATES_AND_IMPROVEMENTS = [
  {
    title: 'In-app notifications',
    body: 'Breeding due dates, banding windows, billing grace / trial endings, and operational alerts surface in the Notifications module for signed-in users.',
  },
  {
    title: 'Email for billing events',
    body: 'When SMTP is configured, payment-failed, trial-ending, and trial-expired messages go to the billing contact or active admins — not marketing spam.',
  },
  {
    title: 'Product improvements',
    body: 'The suite ships continuously (breeding workflows, AI cost controls, genetics, health, marketplace). Material changes that affect entitlements or data handling are reflected in Settings, legal docs, and release notes your operator publishes.',
  },
  {
    title: 'What we will not do',
    body: 'We do not sell your flock data. We do not silently remove modules behind a higher plan — usage meters are the differentiator. AI suggestions are never auto-saved without an operator action.',
  },
];

export const LANDING_HEADLINE =
  'Dedicated software for breeders who run real aviaries — flock, breeding, health, and sales in one place.';

export const LANDING_SUPPORT =
  'BirdMan is a single-tenant suite: your instance, your birds, full modules on every plan. You pay for capacity that costs to run — not for unlocking screens.';

/** Demo landing hero — shown on birdman-demo / evaluator hosts. */
export const DEMO_LANDING_HEADLINE =
  'Explore the full BirdMan suite on a live demo — no shared login required.';

export const DEMO_LANDING_SUPPORT =
  'Create your own guest account with name, email, and password (or Google). You get viewer access to explore birds, breeding, health, finance, and more. User management stays admin-only; your sign-ins are tracked for ops.';
